import React, { useState, useEffect, useCallback } from 'react';
import { motion, AnimatePresence } from 'framer-motion';
import {
  LightBulbIcon,
  ExclamationTriangleIcon,
  CheckCircleIcon,
  InformationCircleIcon,
  ArrowTrendingUpIcon,
  ArrowTrendingDownIcon,
  ClockIcon,
  StarIcon,
  BookOpenIcon,
  UserGroupIcon,
  CurrencyDollarIcon,
  CogIcon,
  ShieldCheckIcon,
  ChatBubbleLeftRightIcon,
  CalendarIcon,
  AcademicCapIcon,
} from '@heroicons/react/24/outline';
import { toast } from 'react-toastify';

const SmartInsight = ({ healthData, trendData, onInsightAction, onScheduleSession }) => {
  const [insights, setInsights] = useState([]);
  const [filteredInsights, setFilteredInsights] = useState([]);
  const [selectedPriority, setSelectedPriority] = useState('all');
  const [selectedCategory, setSelectedCategory] = useState('all');
  const [expandedInsight, setExpandedInsight] = useState(null);

  const priorities = [
    { key: 'all', label: 'All Priorities', color: 'gray', gradient: 'from-gray-500 to-gray-600' },
    { key: 'critical', label: 'Critical', color: 'red', gradient: 'from-red-500 to-red-600' },
    { key: 'high', label: 'High', color: 'orange', gradient: 'from-orange-500 to-orange-600' },
    { key: 'medium', label: 'Medium', color: 'yellow', gradient: 'from-yellow-500 to-yellow-600' },
    { key: 'low', label: 'Low', color: 'green', gradient: 'from-emerald-500 to-emerald-600' },
  ];

  const categories = [
    {
      key: 'all',
      label: 'All Categories',
      icon: LightBulbIcon,
      gradient: 'from-gray-500 to-gray-600',
    },
    {
      key: 'finance',
      label: 'Finance',
      icon: CurrencyDollarIcon,
      gradient: 'from-blue-500 to-blue-600',
    },
    {
      key: 'sales',
      label: 'Sales',
      icon: ArrowTrendingUpIcon,
      gradient: 'from-emerald-500 to-emerald-600',
    },
    {
      key: 'operations',
      label: 'Operations',
      icon: CogIcon,
      gradient: 'from-purple-500 to-purple-600',
    },
    {
      key: 'hr',
      label: 'Human Resources',
      icon: UserGroupIcon,
      gradient: 'from-orange-500 to-orange-600',
    },
    {
      key: 'compliance',
      label: 'Compliance',
      icon: ShieldCheckIcon,
      gradient: 'from-red-500 to-red-600',
    },
    {
      key: 'training',
      label: 'Training',
      icon: AcademicCapIcon,
      gradient: 'from-indigo-500 to-indigo-600',
    },
    {
      key: 'general',
      label: 'General',
      icon: InformationCircleIcon,
      gradient: 'from-gray-500 to-gray-600',
    },
  ];

  const getPriorityIcon = priority => {
    switch (priority) {
      case 'critical':
        return ExclamationTriangleIcon;
      case 'high':
        return ExclamationTriangleIcon;
      case 'medium':
        return InformationCircleIcon;
      case 'low':
        return CheckCircleIcon;
      default:
        return InformationCircleIcon;
    }
  };

  const getPriorityColor = priority => {
    switch (priority) {
      case 'critical':
        return 'bg-red-50 text-red-700 border-red-200';
      case 'high':
        return 'bg-orange-50 text-orange-700 border-orange-200';
      case 'medium':
        return 'bg-amber-50 text-amber-700 border-amber-200';
      case 'low':
        return 'bg-emerald-50 text-emerald-700 border-emerald-200';
      default:
        return 'bg-gray-50 text-gray-700 border-gray-200';
    }
  };

  const getCategoryIcon = category => {
    const cat = categories.find(c => c.key === category);
    return cat?.icon || InformationCircleIcon;
  };

  const getCategoryStyle = category => {
    const cat = categories.find(c => c.key === category);
    return cat || categories[0];
  };

  const generateInsights = useCallback(() => {
    const newInsights = [];
    const currentDate = new Date();

    const overallScore =
      Object.values(healthData).reduce((sum, domain) => {
        return sum + (domain?.score || 0);
      }, 0) / Object.keys(healthData).length;

    if (overallScore < 40) {
      newInsights.push({
        id: 'overall-critical',
        title: 'Critical Business Health Alert',
        description:
          'Your overall business health score is critically low. Immediate action is required.',
        priority: 'critical',
        category: 'general',
        action: 'retake-assessment',
        actionLabel: 'Retake Assessment',
        timestamp: currentDate,
        icon: ExclamationTriangleIcon,
      });
    } else if (overallScore < 60) {
      newInsights.push({
        id: 'overall-warning',
        title: 'Business Health Warning',
        description: 'Your business health needs attention. Consider reviewing key areas.',
        priority: 'high',
        category: 'general',
        action: 'view-checklist',
        actionLabel: 'View Checklist',
        timestamp: currentDate,
        icon: InformationCircleIcon,
      });
    }

    Object.entries(healthData).forEach(([domain, data]) => {
      if (data?.score < 50) {
        const domainName = domain.charAt(0).toUpperCase() + domain.slice(1);
        newInsights.push({
          id: `${domain}-low-score`,
          title: `${domainName} Health Alert`,
          description: `Your ${domain} health score is below 50%. This area needs immediate attention.`,
          priority: 'high',
          category: domain,
          action: 'view-checklist',
          actionLabel: 'Review Checklist',
          timestamp: currentDate,
          icon: getCategoryIcon(domain),
        });
      }
    });

    if (trendData) {
      Object.entries(trendData).forEach(([metric, data]) => {
        if (data && data.length >= 2) {
          const latest = data[data.length - 1]?.value || 0;
          const previous = data[data.length - 2]?.value || 0;
          const change = latest - previous;

          if (change < -10) {
            newInsights.push({
              id: `${metric}-declining`,
              title: `${metric.charAt(0).toUpperCase() + metric.slice(1)} Declining`,
              description: `Your ${metric} health has declined by ${Math.abs(change)}% recently.`,
              priority: 'medium',
              category: metric,
              action: 'view-trends',
              actionLabel: 'View Trends',
              timestamp: currentDate,
              icon: ArrowTrendingDownIcon,
            });
          } else if (change > 10) {
            newInsights.push({
              id: `${metric}-improving`,
              title: `${metric.charAt(0).toUpperCase() + metric.slice(1)} Improving`,
              description: `Great news! Your ${metric} health has improved by ${change}%.`,
              priority: 'low',
              category: metric,
              action: 'view-trends',
              actionLabel: 'View Trends',
              timestamp: currentDate,
              icon: ArrowTrendingUpIcon,
            });
          }
        }
      });
    }
    newInsights.push({
      id: 'general-tip',
      title: 'Regular Health Check',
      description: 'Consider scheduling regular health assessments to track your progress.',
      priority: 'low',
      category: 'general',
      action: 'schedule-session',
      actionLabel: 'Schedule Session',
      timestamp: currentDate,
      icon: CalendarIcon,
    });

    return newInsights;
  }, [healthData, trendData]);

  useEffect(() => {
    const generatedInsights = generateInsights();
    setInsights(generatedInsights);
    setFilteredInsights(generatedInsights);
  }, [generateInsights]);

  useEffect(() => {
    let filtered = insights;

    if (selectedPriority !== 'all') {
      filtered = filtered.filter(insight => insight.priority === selectedPriority);
    }

    if (selectedCategory !== 'all') {
      filtered = filtered.filter(insight => insight.category === selectedCategory);
    }

    setFilteredInsights(filtered);
  }, [insights, selectedPriority, selectedCategory]);

  const handleInsightAction = insight => {
    if (onInsightAction) {
      onInsightAction(insight);
    }

    switch (insight.action) {
      case 'retake-assessment':
        toast.info('Redirecting to Health Assessment...');
        break;
      case 'view-checklist':
        toast.info('Redirecting to Diagnostic Checklist...');
        break;
      case 'view-trends':
        toast.info('Viewing trend data...');
        break;
      case 'schedule-session':
        if (onScheduleSession) {
          onScheduleSession(insight);
        }
        break;
      default:
        toast.info('Processing insight action...');
    }
  };

  const formatTimestamp = timestamp => {
    const now = new Date();
    const diff = now - timestamp;
    const minutes = Math.floor(diff / (1000 * 60));
    const hours = Math.floor(diff / (1000 * 60 * 60));
    const days = Math.floor(diff / (1000 * 60 * 60 * 24));

    if (minutes < 60) return `${minutes}m ago`;
    if (hours < 24) return `${hours}h ago`;
    return `${days}d ago`;
  };

  return (
    <motion.div
      initial={{ opacity: 0, y: 20 }}
      animate={{ opacity: 1, y: 0 }}
      className="rounded-xl border border-gray-200 bg-white p-4 shadow-sm md:p-6"
    >
      <div className="mb-4 flex flex-col items-start justify-between gap-3 sm:flex-row sm:items-center md:mb-6">
        <div className="flex items-center gap-3">
          <div className="flex h-12 w-12 items-center justify-center rounded-xl bg-gradient-to-br from-blue-500 to-blue-600">
            <LightBulbIcon className="h-6 w-6 text-white" />
          </div>
          <div>
            <h3 className="text-xl font-bold text-gray-900">Smart Insights</h3>
            <p className="text-gray-600">AI-powered recommendations for your business health</p>
          </div>
        </div>

        <div className="flex items-center gap-2">
          <span className="text-sm text-gray-500">Total Insights:</span>
          <span className="rounded-full bg-blue-100 px-2 py-1 text-sm font-medium text-blue-800">
            {filteredInsights.length}
          </span>
        </div>
      </div>

      <div className="mb-4 flex flex-wrap items-center gap-3 md:mb-6 md:gap-4">
        <div className="flex items-center gap-2">
          <span className="text-sm font-medium text-gray-700">Priority:</span>
          <div className="flex rounded-lg border border-gray-200 bg-gray-50 p-1">
            {priorities.map(priority => (
              <motion.button
                key={priority.key}
                whileHover={{ scale: 1.05 }}
                whileTap={{ scale: 0.95 }}
                onClick={() => setSelectedPriority(priority.key)}
                className={`rounded-md px-3 py-1.5 text-xs font-medium transition-all duration-200 ${
                  selectedPriority === priority.key
                    ? `bg-gradient-to-r ${priority.gradient} text-white shadow-md`
                    : 'text-gray-600 hover:bg-gray-100'
                }`}
              >
                {priority.label}
              </motion.button>
            ))}
          </div>
        </div>

        <div className="flex items-center gap-2">
          <span className="text-sm font-medium text-gray-700">Category:</span>
          <div className="flex rounded-lg border border-gray-200 bg-gray-50 p-1">
            {categories.map(category => (
              <motion.button
                key={category.key}
                whileHover={{ scale: 1.05 }}
                whileTap={{ scale: 0.95 }}
                onClick={() => setSelectedCategory(category.key)}
                className={`flex items-center gap-1.5 rounded-md px-3 py-1.5 text-xs font-medium transition-all duration-200 ${
                  selectedCategory === category.key
                    ? `bg-gradient-to-r ${category.gradient} text-white shadow-md`
                    : 'text-gray-600 hover:bg-gray-100'
                }`}
              >
                <category.icon className="h-3 w-3" />
                {category.label}
              </motion.button>
            ))}
          </div>
        </div>
      </div>

      <div className="grid grid-cols-1 gap-3 md:grid-cols-2 md:gap-4 lg:grid-cols-3">
        {filteredInsights.map((insight, index) => {
          const InsightIcon = insight.icon;
          const categoryStyle = getCategoryStyle(insight.category);

          return (
            <motion.div
              key={insight.id}
              initial={{ opacity: 0, y: 20 }}
              animate={{ opacity: 1, y: 0 }}
              transition={{ delay: index * 0.1 }}
              whileHover={{ scale: 1.02, y: -2 }}
              className="group cursor-pointer rounded-xl border border-gray-200 bg-white p-4 shadow-sm transition-all duration-200 hover:shadow-md"
            >
              <div className="mb-3 flex items-start justify-between">
                <div className="flex items-center gap-2">
                  <div className={`rounded-lg bg-gradient-to-br p-2 ${categoryStyle.gradient}`}>
                    <InsightIcon className="h-4 w-4 text-white" />
                  </div>
                  <span
                    className={`inline-flex items-center rounded-full px-2 py-1 text-xs font-medium ${getPriorityColor(insight.priority)}`}
                  >
                    {insight.priority}
                  </span>
                </div>
                <span className="text-xs text-gray-500">{formatTimestamp(insight.timestamp)}</span>
              </div>

              <h4 className="mb-2 font-semibold text-gray-900 transition-colors group-hover:text-blue-600">
                {insight.title}
              </h4>
              <p className="mb-3 line-clamp-2 text-sm text-gray-600">{insight.description}</p>

              <motion.button
                whileHover={{ scale: 1.05 }}
                whileTap={{ scale: 0.95 }}
                onClick={() => handleInsightAction(insight)}
                className="w-full rounded-lg bg-gradient-to-r from-blue-500 to-blue-600 px-3 py-2 text-xs font-medium text-white transition-all duration-200 hover:from-blue-600 hover:to-blue-700 hover:shadow-md"
              >
                {insight.actionLabel}
              </motion.button>
            </motion.div>
          );
        })}
      </div>

      {filteredInsights.length === 0 && (
        <motion.div initial={{ opacity: 0 }} animate={{ opacity: 1 }} className="py-12 text-center">
          <div className="mx-auto mb-4 flex h-16 w-16 items-center justify-center rounded-full bg-gray-100">
            <LightBulbIcon className="h-8 w-8 text-gray-400" />
          </div>
          <h3 className="mb-2 text-lg font-medium text-gray-900">No insights found</h3>
          <p className="text-gray-500">
            Try adjusting your filters or complete more assessments to generate insights.
          </p>
        </motion.div>
      )}
    </motion.div>
  );
};

export default SmartInsight;
